import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { format } from "date-fns";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import FeatureGate from "shared/components/FeatureGate";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";

const CareGapsPageInner = () => {
  const { user } = useAuth();
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    setLoadError(false);
    const unsub = db
      .collection("users")
      .doc(user.uid)
      .collection("careGaps")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snap) => {
          setGaps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        () => {
          setLoadError(true);
          setLoading(false);
        },
      );
    return () => unsub();
  }, [user?.uid]);

  const openGaps = gaps.filter((g) => g.status === "open");

  const applyStatus = async () => {
    if (!actionTarget || !user?.uid) return;
    setActionLoading(true);
    try {
      await db
        .collection("users")
        .doc(user.uid)
        .collection("careGaps")
        .doc(actionTarget.id)
        .update({ status: actionTarget.nextStatus });
      setSuccess(
        actionTarget.nextStatus === "completed"
          ? "Care gap marked complete."
          : "Care gap dismissed.",
      );
      setActionTarget(null);
    } catch (err) {
      setSuccess(null);
      setLoadError(true);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <DashboardPageHeader
        title="Care gap reminders"
        subtitle="Follow-ups and screenings your care team wants you to complete."
      />
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <DataLoadErrorAlert />
      ) : openGaps.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          You have no open care gap reminders.
        </Typography>
      ) : (
        <List>
          {openGaps.map((gap) => (
            <ListItem
              key={gap.id}
              alignItems="flex-start"
              secondaryAction={
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={() =>
                      setActionTarget({ id: gap.id, nextStatus: "completed", title: gap.title })
                    }
                  >
                    Complete
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() =>
                      setActionTarget({ id: gap.id, nextStatus: "dismissed", title: gap.title })
                    }
                  >
                    Dismiss
                  </Button>
                </Stack>
              }
            >
              <ListItemText
                primary={gap.title}
                secondary={
                  <>
                    {gap.description || null}
                    {gap.dueAt?.toDate ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Due {format(gap.dueAt.toDate(), "MMM d, yyyy")}
                      </Typography>
                    ) : null}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      <ConfirmDialog
        open={Boolean(actionTarget)}
        title={actionTarget?.nextStatus === "completed" ? "Mark complete?" : "Dismiss reminder?"}
        message={
          actionTarget
            ? `${actionTarget.nextStatus === "completed" ? "Mark" : "Dismiss"} “${actionTarget.title}”?`
            : ""
        }
        confirmColor={actionTarget?.nextStatus === "dismissed" ? "error" : "primary"}
        confirmLabel={actionTarget?.nextStatus === "completed" ? "Complete" : "Dismiss"}
        loading={actionLoading}
        onConfirm={applyStatus}
        onClose={() => setActionTarget(null)}
      />
    </Box>
  );
};

const CareGapsPage = () => (
  <FeatureGate feature="care-gap" showUpgradePrompt>
    <CareGapsPageInner />
  </FeatureGate>
);

export default CareGapsPage;
